import React from 'react';
import { Box } from '@mui/material';

interface CylinderProps {
  radius?: number;
  height?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const Cylinder: React.FC<CylinderProps> = ({
  radius = 2,
  height = 5,
  unit = 'cm',
  color = '#E0FFE0',
  strokeColor = '#9c27b0'
}) => {
  const cx = 50;
  const cy = 50;
  
  const maxDim = Math.max(radius * 2, height);
  const scale = 45 / maxDim; 
  
  const R = radius * scale;
  const H = height * scale;
  
  const rx = R * Math.sqrt(1.5);
  const ry = R * Math.SQRT1_2;
  
  const topY = cy - H / 2;
  const bottomY = cy + H / 2;

  const bodyPath = `M ${cx - rx} ${topY} L ${cx - rx} ${bottomY} A ${rx} ${ry} 0 0 0 ${cx + rx} ${bottomY} L ${cx + rx} ${topY} A ${rx} ${ry} 0 0 1 ${cx - rx} ${topY} Z`;
  const backBottomPath = `M ${cx - rx} ${bottomY} A ${rx} ${ry} 0 0 1 ${cx + rx} ${bottomY}`;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        <defs>
          <linearGradient id="cylinderGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity={0.7} />
            <stop offset="50%" stopColor={color} stopOpacity={0.8} />
            <stop offset="100%" stopColor={color} stopOpacity={0.9} />
          </linearGradient>
        </defs>

        {/* Hidden edges (dashed) */}
        <path d={backBottomPath} fill="none" stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />

        {/* Top face */}
        <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill={color} opacity={0.4} />

        {/* Body */}
        <path d={bodyPath} fill="url(#cylinderGrad)" />

        {/* Visible Edges */}
        <path d={`M ${cx - rx} ${topY} L ${cx - rx} ${bottomY} A ${rx} ${ry} 0 0 0 ${cx + rx} ${bottomY} L ${cx + rx} ${topY}`} fill="none" stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill="none" stroke={strokeColor} strokeWidth={1.5} />

        {/* Points */}
        <circle cx={cx} cy={topY} r={1.2} fill={strokeColor} />
        
        {/* Dimension labels */}
        {/* r (radius) */}
        <line x1={cx} y1={topY} x2={cx + rx} y2={topY} stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} />
        <text x={cx + rx / 2} y={topY - 2} fontSize={3.5} fill="#374151" textAnchor="middle" fontWeight={600}>r = {radius} {unit}</text>

        {/* h (height) */}
        <text x={cx - rx - 4} y={cy + 1.5} fontSize={3.5} fill="#374151" textAnchor="end" fontWeight={600}>h = {height} {unit}</text>
      </svg>
    </Box>
  );
};

export default Cylinder;
